import {ChangeDetectorRef, Component, Inject, Injector, OnInit, PLATFORM_ID, Type} from "@angular/core";
import {OfferJourneyService} from "../../services/offer-journey/offer-journey.service";
import {debounceTime, takeUntil, tap} from "rxjs/operators";
import {FELLO_JOURNEY_TASK_ID, FELLO_JOURNEY_TASK_SCREEN_MAPPING} from "../../injection-token";
import {combineLatest} from "rxjs";
import {forEach, memoize} from "lodash-es";
import {ActivatedRoute} from "@angular/router";
import {OfferFlowTrackingEventService} from "./offer-flow-tracking-event.service";
import {GlobalTrackingEventService, mixinDestroyable} from "fello-common";
import {JourneySettingsService} from "../../services/journey-settings.service";
import {DOCUMENT, isPlatformBrowser} from "@angular/common";

export interface QuestionComponent {
  injector: Injector;
  type: Type<unknown>;
}

@Component({
  selector: "app-offer-flow-wrapper",
  templateUrl: "./offer-flow-wrapper.component.html",
  styleUrls: ["./offer-flow-wrapper.component.scss"],
  providers: [
    OfferJourneyService,
    OfferFlowTrackingEventService,
    {
      provide: GlobalTrackingEventService,
      useExisting: OfferFlowTrackingEventService
    },
    JourneySettingsService
  ]
})
export class OfferFlowWrapperComponent extends mixinDestroyable(class {}) implements OnInit {
  allQuestions: QuestionComponent[] = [];
  journeySettings$ = this.journeySettingsService.settings$;

  allQuestionsTrackBy = (index: number, question: QuestionComponent) => {
    return question.type;
  };

  constructor(
    private offerJourneyService: OfferJourneyService,
    private inj: Injector,
    private cd: ChangeDetectorRef,
    @Inject(FELLO_JOURNEY_TASK_SCREEN_MAPPING) private screenNameMapping: Record<string, Type<unknown>>,
    private activatedRoute: ActivatedRoute,
    private trackingEventService: OfferFlowTrackingEventService,
    private journeySettingsService: JourneySettingsService,
    @Inject(DOCUMENT) private document: Document,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    super();
    this.getInjector = memoize(this.getInjector);
    const disableTracking = this.activatedRoute.snapshot.queryParamMap.has("_dt");
    if (disableTracking) {
      this.trackingEventService.disabledTracking();
    } else {
      this.trackingEventService.enableTracking();
    }
  }

  ngOnInit(): void {
    combineLatest([this.offerJourneyService.felloOfferJourney, this.journeySettings$])
      .pipe(
        debounceTime(10),
        takeUntil(this.isDestroyed),
        tap(([offerJourney, journeySettings]) => {
          const taskIdToScreenNameMapping = journeySettings!.taskIdToScreenMapping;
          this.allQuestions = [];
          forEach(offerJourney.userTrail, task => {
            const question = {
              injector: this.getInjector(task),
              type: this.screenNameMapping[taskIdToScreenNameMapping[task]]
            };
            this.allQuestions.push(question);
          });
          const currentQuestion = {
            injector: this.getInjector(offerJourney.next),
            type: this.screenNameMapping[taskIdToScreenNameMapping[offerJourney.next]]
          };
          this.allQuestions.push(currentQuestion);
          this.cd.detectChanges();
          if (isPlatformBrowser(this.platformId)) {
            setTimeout(() => {
              const el = this.document.getElementById(offerJourney.next);
              if (el) {
                el.scrollIntoView({behavior: "smooth", block: "nearest", inline: "start"});
              }
            }, 500);
          }
        })
      )
      .subscribe();
  }

  private getInjector(id: string): Injector {
    const options = {
      providers: [{provide: FELLO_JOURNEY_TASK_ID, useValue: id}],
      parent: this.inj
    };
    const injector = Injector.create(options);
    return injector;
  }
}
